import { useEffect, useRef, useState } from 'react';
import { DURATION } from '../../motion/tokens';

/* The numeric token, not parseInt(duration.fast): same number (160), with
   no string parsing between the scale and the timer. */
const SELECTION_DELAY_MS = DURATION.fast;

/**
 * HOW LONG TO WAIT BEFORE DECIDING THE NAVIGATION DID NOT HAPPEN.
 *
 * A real navigation unmounts this hook, and the cleanup below cancels this
 * timer, so it only ever fires when we are STILL ON THE SAME SCREEN a moment
 * after advancing — i.e. the navigation was refused, bounced, or was to the
 * path we are already on. Generous enough that it cannot race a slow route
 * transition into re-enabling a menu mid-navigation.
 */
const LOCK_RELEASE_MS = 1200;

interface UseOptionAdvanceOptions {
  onSelect: (value: string) => void;
  onAdvance: (value: string) => void;
}

/**
 * Select an option, show it as pending, then advance — once.
 *
 * ── WHY THE LOCK EXISTS ──
 * Double-tapping a menu tile used to fire two navigations, and on the wizard
 * screens that meant skipping a step. One advance per press is the contract.
 *
 * ── WHY THE LOCK NOW RELEASES, WHICH IT DID NOT ──
 * `lockRef` was set on the first press and never cleared. That was safe only
 * under the assumption that advancing always unmounts the hook — and it does
 * not. A route guard that refuses the target renders <Navigate> straight back
 * to where we came from, so the menu re-mounts... except when it does not,
 * because the target resolved to the SAME path. In that case the lock stayed
 * set forever and every subsequent tile on that screen was dead, with no error
 * and nothing in the server log, because no request was ever made.
 *
 * That is the shape of "I clock out and then any button stops working": one
 * refused navigation, and the menu is inert until the page is reloaded. The
 * refusal itself is fixed separately (a concierge holds report:read and was
 * being bounced off /view-report by a role gate stricter than the server's),
 * but a UI that can be bricked by a guard doing its job is a bug on its own.
 */
export const useOptionAdvance = ({
  onSelect,
  onAdvance,
}: UseOptionAdvanceOptions) => {
  const lockRef = useRef(false);
  const advanceTimer = useRef<number | undefined>(undefined);
  const releaseTimer = useRef<number | undefined>(undefined);
  const [pendingValue, setPendingValue] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (advanceTimer.current !== undefined) {
        window.clearTimeout(advanceTimer.current);
      }
      // The one that matters on unmount: a successful navigation tears this
      // hook down, and cancelling here is what stops the release below from
      // ever running for a press that DID leave the screen.
      if (releaseTimer.current !== undefined) {
        window.clearTimeout(releaseTimer.current);
      }
    },
    []
  );

  const selectAndAdvance = (value: string) => {
    if (lockRef.current) {
      return;
    }

    lockRef.current = true;
    setPendingValue(value);
    onSelect(value);
    advanceTimer.current = window.setTimeout(() => {
      onAdvance(value);

      // Still here? Then the advance did not take us anywhere and the screen is
      // the same one the user is looking at. Give the menu back.
      releaseTimer.current = window.setTimeout(() => {
        lockRef.current = false;
        setPendingValue(null);
      }, LOCK_RELEASE_MS);
    }, SELECTION_DELAY_MS);
  };

  return { pendingValue, selectAndAdvance };
};
